import React from 'react'

// Catches render errors thrown by JobCard so a single broken job (e.g. a
// CSV-imported row with no bid_id, malformed client_name, or null fields the
// downstream code wasn't written to expect) doesn't unmount the whole Jobs
// route and bounce the user back to the dashboard.
//
// "Show details anyway" re-mounts the child with safeMode=true, which the
// child uses to skip the quote_snapshot panels (the most likely source of
// shape-mismatch crashes on legacy rows that pre-date the snapshot column).
export default class JobDetailErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, safeMode: false }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error: error }
  }

  componentDidCatch(error, info) {
    var jobId = (this.props.job && this.props.job.id) || null
    var jobNumber = (this.props.job && this.props.job.job_number) || null
    console.error('JobDetail crash:', { job_id: jobId, job_number: jobNumber, error: error, info: info })
  }

  reset = () => this.setState({ hasError: false, error: null, safeMode: false })

  enterSafeMode = () => this.setState({ hasError: false, error: null, safeMode: true })

  render() {
    if (!this.state.hasError) {
      // Pass safeMode through if the user opted in via "Show details anyway".
      if (this.state.safeMode && React.Children.count(this.props.children) === 1) {
        return React.Children.map(this.props.children, function(child) {
          return React.isValidElement(child) ? React.cloneElement(child, { safeMode: true }) : child
        })
      }
      return this.props.children
    }
    var msg = (this.state.error && this.state.error.message) || 'Unable to load job details.'
    var jobNumber = (this.props.job && this.props.job.job_number) || ''
    var clientName = (this.props.job && this.props.job.client_name) || ''
    var headline = jobNumber || clientName
      ? 'This job has incomplete data — ' + [jobNumber, clientName].filter(Boolean).join(' ')
      : 'This job has incomplete data'
    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <div style={styles.title}>{headline}</div>
          <div style={styles.msg}>{msg}</div>
          <div style={styles.hint}>
            The page didn't crash — but the job detail view couldn't render. This usually means
            data is missing or shaped differently than expected (e.g. no linked bid, no quote
            snapshot, malformed customer info). The rest of the app is still usable.
          </div>
          <div style={styles.actions}>
            <button onClick={this.props.onClose || (function() { window.history.back() })} style={styles.btnSecondary}>CLOSE</button>
            <button onClick={this.reset} style={styles.btnSecondary}>TRY AGAIN</button>
            <button onClick={this.enterSafeMode} style={styles.btnPrimary}>SHOW DETAILS ANYWAY</button>
          </div>
        </div>
      </div>
    )
  }
}

const styles = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 400, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', padding: '20px', fontFamily: "'Barlow',sans-serif" },
  card: { width: '480px', maxWidth: '100%', backgroundColor: '#0d1420', border: '1px solid rgba(224,92,92,0.4)', borderRadius: '4px', padding: '28px', display: 'flex', flexDirection: 'column', gap: '14px' },
  title: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '20px', fontWeight: 700, letterSpacing: '2px', color: '#e05c5c' },
  msg: { fontSize: '14px', color: '#ffffff', lineHeight: 1.5, padding: '12px', backgroundColor: 'rgba(224,92,92,0.08)', border: '1px solid rgba(224,92,92,0.25)', borderRadius: '3px', wordBreak: 'break-word' },
  hint: { fontSize: '12px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 },
  actions: { display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px', flexWrap: 'wrap' },
  btnSecondary: { padding: '10px 16px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '3px', color: 'rgba(255,255,255,0.55)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  btnPrimary: { padding: '10px 18px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
}
